import gql from 'graphql-tag';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { Booking, CreditCard, Payline, PaymentProvider, helpers } from '@aps-management/primapp-common';

import Payment from '_components/Payment';
import { Screen } from '_components/core';
import { DialogPaymentIP, PaylineWidgetFrame } from '_components/elements';
import DialogWallet3DS from '_components/elements/DialogWallet3DS';
import apolloClient from '_utils/apolloClient';
import { buildBookingDeliveryIntent } from '_utils/deliveryIntent';

import { PAYLINE_FLOW_TYPES, setupPaylineListeners } from '../../../services/payline';
import LockedSlotReminder from '../components/LockedSlotReminder';
import OverviewHeader from '../components/OverviewHeader';

/* */

/*
 * Message de l'état intermédiaire « payé mais pas encore confirmé ». Il doit dire
 * trois choses et rien d'autre : le paiement est en cours de vérification, il ne
 * faut pas repayer, et quelqu'un s'en occupe.
 *
 * Il n'affirme PAS que l'argent est arrivé, et c'est délibéré : `paid: true` couvre
 * aussi la capture ENCORE EN VOL, que la banque peut refuser. Promettre un
 * encaissement dans ce cas serait faux. À l'inverse, toute formulation qui
 * laisserait croire à un échec pousserait le golfeur à recommencer, donc à payer
 * deux fois. Le message doit rester vrai dans les quatre situations.
 */
const PAID_NOT_CONFIRMED_MESSAGE = 'Votre paiement est en cours de vérification, et votre réservation n’est pas encore confirmée. '
  + 'Ne renouvelez pas l’opération : vous recevrez votre bon de réservation dès que la confirmation aboutit. '
  + 'En cas de doute, contactez l’accueil du golf.';

const BookingPayment = (props) => {
  const {
    golf,
    account,
    data: { booking },
    history,
    setLockedSlot,
    updateBooking,
  } = props;

  // Redirect if no booking
  const redirectRef = useRef(booking === null);

  // Derived constants
  const amount = useMemo(() => booking?.total?.price, [booking]);
  const currency = useMemo(() => booking?.total?.currency, [booking]);
  const session3DS = useMemo(
    () => ({
      address: {
        city: golf?.city,
        countryCode: 250,
        line1: golf?.name,
        zipCode: golf?.postalCode,
      },
      customer: {
        email: account?.email,
        lastName: account?.lastname,
        firstName: account?.firstname,
        phone: account?.phone,
      },
    }),
    [golf, account],
  );

  // State
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waiting, setWaiting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [imprint, setImprint] = useState(null);
  const [paylineToken, setPaylineToken] = useState(null);
  const [wallet3DS, setWallet3DS] = useState({
    open: false,
    threeDSData: null,
    subscriber: null,
    amount: null,
    reference: null,
  });

  useEffect(() => {
    if (account?.id) {
      CreditCard.api
        .getCreditCards(apolloClient, { accountId: account.id })
        .then(({ creditCards }) => {
          // Garde-fou : seule une carte disposant d'un wallet Payline réel (walletId) peut
          // être débitée en 1 clic. Une carte sans walletId (enregistrée avant le correctif
          // ou via un chemin legacy) produirait subscriber.id=null : mutation rejetée. On ne
          // la propose donc pas : l'utilisateur ressaisit sa carte (le widget crée un vrai
          // wallet), ce qui répare la situation de lui-même.
          const usable = (creditCards || []).find((c) => c && c.walletId);
          if (usable) {
            setImprint(usable);
          }
        })
        .catch(() => {})
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [account]);

  // Refs for async tracking
  const paylineWidgetDataRef = useRef(null);
  const isPaylinePaymentHandledRef = useRef(false);
  const removePaylineListenersRef = useRef(null);
  // Verrou de ré-entrée SYNCHRONE : un double message ACS (POST 3DS) ne doit pas rejouer le débit.
  const is3DSCompletingRef = useRef(false);

  /*
   * Vrai dès que de l'argent est parti sans que la réservation soit confirmée. Porté
   * par une ref et non par l'état, pour être lu de façon synchrone au tout début
   * d'une tentative : un état ne serait pas encore à jour au second clic.
   */
  const paymentCapturedRef = useRef(false);
  const [paidNotConfirmed, setPaidNotConfirmed] = useState(false);

  // Mount
  useEffect(() => {
    console.log('BookingPayment mounted', props);
  }, [props]);

  /*
   * Démontage : `setupPaylineListeners` pose ses écouteurs sur `window`, pas sur le
   * composant. Sans cette libération ils survivent à l'écran, et un aller-retour
   * (retour au panier puis nouvelle tentative) laisse deux instances à l'écoute du
   * même widget : au succès, la chaîne de confirmation part DEUX fois, chacune avec
   * ses propres refs, donc sans que les verrous de ré-entrée par instance
   * (`isPaylinePaymentHandledRef`, `is3DSCompletingRef`) ne voient quoi que ce soit.
   * Cf. incident Golf aux Daims du 25/07/2026, un débit et deux crédits.
   *
   * Dépendances vides : ce nettoyage ne doit tourner qu'au démontage réel, jamais
   * entre deux rendus - il couperait l'écoute d'un paiement en cours.
   */
  useEffect(
    () => () => {
      if (removePaylineListenersRef.current) {
        removePaylineListenersRef.current();
        removePaylineListenersRef.current = null;
      }
    },
    [],
  );

  // Navigation vers le voucher (mutualisée avec les chemins de succès).
  const goToVoucher = (data, transaction) => {
    updateBooking({ ...data, transaction });
    setLockedSlot(null);
    history.replace('/booking/voucher');
  };

  // Récupération avant annulation : après un échec/timeout de capture, le débit a
  // peut-être réussi côté Payline sans que la réponse nous revienne. On demande au
  // serveur de confirmer à partir du paiement déjà encaissé (bookingConfirmIfCaptured,
  // idempotent). Si payé ET confirmé -> voucher ; sinon -> annulation comme avant.
  // Termine toujours par un throw {handled:true} pour stopper la chaîne (cf. incident
  // LAZZARETTO 19/07).
  const recoverOrCancel = async (data, err) => {
    if (data && data.id) {
      setStatus('Vérification du paiement...');
      const recovery = await Booking.api
        .bookingConfirmIfCaptured(apolloClient, { id: data.id })
        .catch(() => null);
      if (recovery && recovery.paid) {
        /*
         * `paid: true` ne veut PAS dire « réservation confirmée ». Le serveur s'en
         * sert aussi pour dire « ne surtout pas annuler » sur trois abstentions -
         * verrou de résolution contesté, plusieurs sessions payées sur la même
         * commande, capture encore en vol - et il répond alors `status: 'pending'`
         * SANS transaction. Lire le seul `paid` envoyait le golfeur sur son bon de
         * réservation, c'est-à-dire une confirmation affichée pour une réservation
         * qui n'est pas confirmée et que le golf ne verra pas arriver.
         *
         * Le bon de réservation n'est donc délivré que sur `confirmed`, le seul
         * état que le serveur pose après avoir réellement confirmé la commande.
         */
        if (recovery.status === 'confirmed') {
          goToVoucher(data, recovery.transaction);
          throw Object.assign(new Error('recovered'), { handled: true });
        }
        /*
         * Payé mais pas confirmé : surtout ne pas annuler (ce serait annuler une
         * réservation encaissée), surtout ne pas afficher de bon. On s'arrête en le
         * disant, et on laisse la reprise serveur faire son travail.
         */
        /*
         * On VERROUILLE l'écran, et ce n'est pas décoratif : sans lui, le golfeur
         * retrouve un formulaire payable. S'il relance, une commande neuve est
         * créée, donc une référence neuve, et le verrou anti-doublon du serveur -
         * indexé sur la référence - ne s'y oppose plus. Second débit réel.
         */
        paymentCapturedRef.current = true;
        setPaidNotConfirmed(true);
        setError(PAID_NOT_CONFIRMED_MESSAGE);
        setStatus(null);
        setWaiting(false);
        throw Object.assign(new Error(PAID_NOT_CONFIRMED_MESSAGE), { handled: true });
      }
    }
    setStatus('Erreur lors de la transaction, annulation en cours...');
    return Booking.api
      .bookingCancel(apolloClient, { id: data?.id, failed: true })
      .catch(() => {})
      .then(() => {
        throw Object.assign(err, { handled: true });
      });
  };

  /**
   * Refuse une nouvelle tentative quand l'argent est déjà parti.
   *
   * Rend `true` si le paiement doit être refusé. Le message est reposé à chaque
   * tentative refusée pour que l'écran ne paraisse jamais mort, et un pavé
   * persistant est rendu en plus (cf. le rendu) : un message seul, sur un écran
   * resté payable, est précisément ce qui fabrique un second débit.
   */
  const refuseIfAlreadyCaptured = () => {
    if (!paymentCapturedRef.current) return false;
    setWaiting(false);
    setStatus(null);
    setPaidNotConfirmed(true);
    /*
     * Remise à `null` PUIS repose au tour suivant, et non les deux d'affilée. React
     * regroupe les mises à jour d'un même tour : reposer la chaîne identique ne
     * changerait rien, `Screen` compare `error !== prevProps.error` et n'afficherait
     * aucun message. Le golfeur cliquerait « Payer » sans voir la moindre réaction,
     * ce qui est précisément l'écran mort qu'on veut éviter.
     */
    setError(null);
    setTimeout(() => setError(PAID_NOT_CONFIRMED_MESSAGE), 0);
    return true;
  };

  /**
   * Traite les dénouements où le SERVEUR a tranché le sort de l'argent.
   *
   * Depuis que master refuse de confirmer une commande annulée entre-temps par le cron
   * `cancelAbandonedBookings`, `bookingConfirm` peut rendre deux verdicts qui ne sont ni
   * un succès ni une panne quelconque :
   *
   *  - `errors.payment.reversed` : la compensation a RENDU l'argent et libéré le
   *    créneau. Il n'y a plus de réservation, donc surtout pas de bon à afficher, et le
   *    golfeur peut légitimement recommencer : on relâche le verrou, sinon il resterait
   *    bloqué devant un message qui l'invite à réessayer ;
   *  - `errors.payment.compensation_failed` : l'inversion n'a PAS abouti. L'argent est
   *    peut-être encore débité, donc on garde le verrou et le pavé persistant.
   *
   * Sans ce traitement, les deux repartaient sur `return data` et l'écran naviguait vers
   * `/booking/voucher` : golfeur remboursé, bon de réservation en main, aucune
   * réservation au golf.
   *
   * Jumeau exact de `handleServerPaymentOutcome` de `primapp-native`. L'embed, lui, n'a
   * pas besoin du même correctif : sur erreur il passe par `recoverOrCancel`, qui
   * n'affiche jamais de bon sans confirmation.
   *
   * @returns {boolean} vrai si le cas a été traité et qu'il ne faut PAS continuer
   */
  const handleServerPaymentOutcome = (err) => {
    const cle = err && typeof err.message === 'string' ? err.message : '';
    if (!cle.startsWith('errors.payment.')) return false;

    const rembourse = cle === 'errors.payment.reversed';
    paymentCapturedRef.current = !rembourse;

    setWaiting(false);
    setStatus(null);
    setLockedSlot(null);
    setPaidNotConfirmed(!rembourse);
    // Même raison qu'au-dessus : `Screen` compare l'ancienne et la nouvelle valeur, et
    // un message reposé dans le même tour de rendu ne s'afficherait pas.
    setError(null);
    setTimeout(() => setError(cle), 0);
    return true;
  };

  const payWithCardImprint = () => {
    // PREMIÈRE instruction : l'argent est peut-être déjà parti sur une tentative
    // précédente restée sans confirmation.
    if (refuseIfAlreadyCaptured()) return Promise.resolve();

    // Garde-fou (défense en profondeur) : ne jamais tenter un paiement 1-clic avec une
    // carte dépourvue de wallet Payline réel. Normalement déjà filtré au chargement.
    if (!imprint || !imprint.walletId) {
      setError(
        "Cette carte enregistrée n'est plus utilisable pour le paiement en 1 clic. Réglez avec une nouvelle carte : elle sera ré-enregistrée automatiquement.",
      );
      return Promise.resolve();
    }

    const subscriber = {
      id: imprint.walletId,
      token: imprint.token,
      dueDate: imprint.dueDate,
    };

    const data = {};

    setError(null);
    setWaiting(true);
    setStatus('Nous prenons votre réservation au golf...');

    return Booking.api
      .bookingBook(apolloClient, booking)
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        data.id = res.id;
        data.amount = res.total.price;
        setStatus('Nous interrogeons votre banque...');

        const captureApi = Payline.api.captureSubscriber;

        return captureApi(apolloClient, {
          amount: data.amount,
          reference: data.id,
          subscriber,
        }).then((res) => {
          // Handle 3DS challenge required for wallet payments
          if (res && res.needs3DS) {
            if (res.threeDSData?.actionUrl) {
              // ACS redirect URL present: show the 3DS challenge dialog
              setWaiting(false);
              setStatus(null);
              setWallet3DS({
                open: true,
                threeDSData: res.threeDSData,
                subscriber,
                amount: data.amount,
                reference: data.id,
              });
              const cancelErr = new Error('3DS_CHALLENGE_REQUIRED');
              cancelErr.is3DS = true;
              throw cancelErr;
            }
            // No ACS URL (doImmediateWalletPayment cannot provide one): fall back
            // to the Payline hosted widget with the saved card pre-selected.
            // The widget handles 3DS natively; success flows through handlePaylinePaymentSuccess.
            setStatus('Chargement du formulaire de paiement...');
            const baseUrl = window.location.origin;
            return Payline.api.getToken(apolloClient, {
              amount: data.amount,
              reference: data.id,
              walletId: subscriber.id,
              returnUrl: `${baseUrl}/booking/voucher`,
              cancelUrl: `${baseUrl}/booking/payment`,
              callbackUrl: `${baseUrl}/payline/callback`,
              customer: {
                firstName: account.firstname,
                lastName: account.lastname,
                email: account.email,
                phone: account.phone,
              },
            }).then((tokenRes) => {
              if (!tokenRes?.token) throw new Error('Token de paiement introuvable.');
              paylineWidgetDataRef.current = data;
              isPaylinePaymentHandledRef.current = false;
              if (removePaylineListenersRef.current) removePaylineListenersRef.current();
              removePaylineListenersRef.current = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
                onSuccess: (eventData) => handlePaylinePaymentSuccess(eventData),
                onFailure: (eventData) => handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
                onCancel: (eventData) => handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
              });
              setPaylineToken(tokenRes.token);
              setWaiting(false);
              setStatus(null);
              // Stop the original promise chain: the widget callback handles booking confirmation.
              const stopErr = new Error('WIDGET_FALLBACK');
              stopErr.is3DS = true;
              throw stopErr;
            });
          }
          return res;
        });
      })
      .catch((err) => {
        if (!err.handled) {
          // Avant d'annuler : le débit a peut-être réussi sans réponse (perte réseau).
          // On tente de récupérer la réservation au lieu de l'annuler.
          return recoverOrCancel(data, err);
        }
        throw err;
      })
      .then((res) => {
        data.transaction = {
          amount: data.amount,
          card: imprint.number,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          creditCardId: imprint.id,
          transactionNo: res.transactionId || res.NUMTRANS,
          ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
        };

        return Booking.api.bookingConfirm(apolloClient, {
          id: data.id,
          transaction: data.transaction,
        });
      })
      .catch((err) => {
        if (!err.handled) {
          // Le serveur a tranché le sort de l'argent : ni voucher, ni repli silencieux.
          if (handleServerPaymentOutcome(err)) {
            throw Object.assign(err, { handled: true });
          }
          return data;
        }
        setError(err.message);
        setWaiting(false);
        setLockedSlot(null);

        throw err;
      })
      .then(() => {
        updateBooking({
          ...data,
          transaction: data.transaction,
        });
        setLockedSlot(null);
        history.replace('/booking/voucher');
      })
      .catch((err) => {
        // 3DS challenge in progress - don't show error
        if (err && err.is3DS) {
          return;
        }
        if (!err.handled) {
          setError(err.message);
          setWaiting(false);
        }
      });
  };

  const handleWallet3DSClose = () => {
    setWallet3DS({
      open: false,
      threeDSData: null,
      subscriber: null,
      amount: null,
      reference: null,
    });
    setError("Authentification 3D Secure annul\u00e9e.");
  };

  const handleWallet3DSComplete = ({ pares, md }) => {
    if (is3DSCompletingRef.current) return;
    is3DSCompletingRef.current = true;
    setWallet3DS({
      open: false,
      threeDSData: null,
      subscriber: null,
      amount: null,
      reference: null,
    });
    setWaiting(true);
    setStatus('Nous interrogeons votre banque...');

    const newData = {};

    Payline.api
      .captureSubscriberAfter3DS(apolloClient, {
        amount: wallet3DS.amount,
        reference: wallet3DS.reference,
        walletId: wallet3DS.subscriber.id,
        pares,
        md,
      })
      .then((res) => {
        newData.transaction = {
          amount: wallet3DS.amount,
          card: imprint.number,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          creditCardId: imprint.id,
          transactionNo: res.transactionId || res.NUMTRANS,
          ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
        };

        return Booking.api.bookingConfirm(apolloClient, {
          id: wallet3DS.reference,
          transaction: newData.transaction,
        });
      })
      .then(() => {
        updateBooking({
          ...newData,
          transaction: newData.transaction,
        });
        setLockedSlot(null);
        history.replace('/booking/voucher');
      })
      .catch((err) => {
        /*
         * Retour du défi 3DS sur carte enregistrée. Ce `catch` n'appelait ni
         * `recoverOrCancel` ni le verrou : il posait un message sur un écran resté
         * payable. Depuis le lot master, un défi qui traîne au-delà de quinze minutes
         * fait annuler la commande par le cron, la capture aboutit quand même, et
         * `bookingConfirm` refuse. Le golfeur lisait « Rapprochez-vous de votre golf »
         * et pouvait cliquer à nouveau, donc payer une seconde fois.
         */
        if (handleServerPaymentOutcome(err)) return;
        setStatus(null);
        setWaiting(false);
        setError(err.message || 'Le paiement a \u00e9chou\u00e9.');
      })
      .finally(() => {
        is3DSCompletingRef.current = false;
      });
  };

  const capture = (data, creditCard) => {
    // Ne persiste que si une carte est réellement créée, pour être reporté dans
    // la transaction (sinon credit_card_id resterait NULL).
    data.newCreditCardId = null;

    if (creditCard.saveImprint) {
      const uuid = helpers.uuid.createUuid();
      data.newCreditCardId = uuid;

      return Payline.api
        .subscribeAndCapture(apolloClient, {
          uuid,
          id3D: data.id3D,
          amount: data.amount,
          reference: account.id,
          creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
        })
        .catch((err) => {
          throw Object.assign(err, { onSubscribe: true });
        })
        .then((res) =>
          CreditCard.api
            .setCreditCard(apolloClient, {
              id: uuid,
              token: res.token,
              number: CreditCard.functions.anonymizeNumber(creditCard.number),
              ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'country', 'brand', 'type']),
            })
            .then(() => res),
        )
        .catch((err) => {
          if (!err.onSubscribe) {
            throw Object.assign(err, {
              message: "Impossible d'enregistrer la carte. Veuillez réessayer ultérieurement.",
            });
          }
          throw err;
        });
    }

    return Payline.api.capture(apolloClient, {
      id3D: data.id3D,
      reference: data.id,
      amount: data.amount,
      creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
    });
  };

  const payWithNewCard = (values, { setSubmitting }) => {
    /*
     * Verrou anti-second-débit. Ce chemin est déclenché UNIQUEMENT par un clic du
     * golfeur, et c'est le seul bouton payable de l'écran quand aucune carte n'est
     * enregistrée : sans cette garde, le verrou n'aurait aucun effet sur lui.
     */
    if (refuseIfAlreadyCaptured()) return Promise.resolve();

    const { id3D = null } = values;

    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    const data = { id3D };

    setError(null);
    setWaiting(true);
    setStatus('Nous prenons votre réservation au golf...');

    return Booking.api
      .bookingBook(apolloClient, booking)
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        data.id = res.id;
        data.amount = res.total.price;
        setStatus('Nous interrogeons votre banque...');

        return capture(data, creditCard);
      })
      .catch((err) => {
        if (!err.handled) {
          // Récupération avant annulation (le débit a peut-être réussi sans réponse).
          return recoverOrCancel(data, err);
        }
        throw err;
      })
      .then((res) => {
        data.transaction = {
          amount: data.amount,
          callNo: res.authorizationNumber || res.NUMAPPEL,
          transactionNo: res.transactionId || res.NUMTRANS,
          card: CreditCard.functions.anonymizeNumber(creditCard.number),
          // Rattache le paiement à la carte enregistrée à la volée (null sinon).
          creditCardId: data.newCreditCardId || null,
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        };

        return Booking.api.bookingConfirm(apolloClient, {
          id: data.id,
          transaction: data.transaction,
        });
      })
      .catch((err) => {
        if (!err.handled) {
          // Le serveur a tranché le sort de l'argent : ni voucher, ni repli silencieux.
          if (handleServerPaymentOutcome(err)) {
            throw Object.assign(err, { handled: true });
          }
          return data;
        }
        setError(err.message);
        setWaiting(false);
        setLockedSlot(null);

        throw err;
      })
      .then(() => {
        updateBooking({
          ...data,
          transaction: data.transaction,
        });
        setLockedSlot(null);
        history.replace('/booking/voucher');
      })
      .catch((err) => {
        if (!err.handled) {
          setError(err.message);
          setWaiting(false);
        }
        setSubmitting(false);
      });
  };

  const payWithNewCardPayline = () => {
    /*
     * Verrou anti-second-débit. Ce chemin est déclenché UNIQUEMENT par un clic du
     * golfeur, et c'est le seul bouton payable de l'écran quand aucune carte n'est
     * enregistrée : sans cette garde, le verrou n'aurait aucun effet sur lui.
     */
    if (refuseIfAlreadyCaptured()) return Promise.resolve();

    const data = {};
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    setError(null);
    setWaiting(true);
    setStatus('Nous prenons votre réservation au golf...');

    return Booking.api
      .bookingBook(apolloClient, booking)
      .catch((err) => {
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        console.log('Booking booked, preparing Payline payment', { bookingId: res.id, total: res.total });

        data.id = res.id;
        data.amount = res.total.price;
        setStatus('Nous préparons le paiement...');

        const baseUrl = window.location.origin;
        const timestamp = Number(new Date());

        const shortId = data.id.split('-')[0];
        return Payline.api.getToken(apolloClient, {
          amount: data.amount,
          reference: `BKG-${shortId}-${timestamp}`,
          /*
           * Désigne la commande à confirmer si le navigateur ne revient pas. La
           * référence ci-dessus n'en porte que les 8 premiers caractères, qui
           * collisionnent sur 1,1 % des commandes ; l'intention porte l'uuid
           * complet, donc une désignation exacte.
           */
          deliveryIntent: buildBookingDeliveryIntent({ orderId: data.id, golf }),
          returnUrl: `${baseUrl}/booking/voucher`,
          cancelUrl: `${baseUrl}/booking/payment`,
          callbackUrl: `${baseUrl}/payline/callback`,
          customer: {
            firstName: account.firstname,
            lastName: account.lastname,
            email: account.email,
            phone: account.phone,
          },
        });
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        data.token = res.token;
        setPaylineToken(data.token);
        setWaiting(false);
        setStatus(null);

        paylineWidgetDataRef.current = data;
        isPaylinePaymentHandledRef.current = false;

        if (removePaylineListenersRef.current) {
          removePaylineListenersRef.current();
        }

        removePaylineListenersRef.current = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
          onSuccess: (eventData) => handlePaylinePaymentSuccess(eventData),
          onFailure: (eventData) => handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
          onCancel: (eventData) => handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
        });
      })
      .catch((err) => {
        if (!err.handled && data.id) {
          // Récupération avant annulation (le débit a peut-être réussi sans réponse).
          return recoverOrCancel(data, err);
        }

        throw err;
      })
      .catch((err) => {
        setError(err.message);
        setWaiting(false);
        setStatus(null);
        setPaylineToken(null);
        paylineWidgetDataRef.current = null;
        setLockedSlot(null);
      });
  };

  const handlePaylinePaymentSuccess = (eventData = {}) => {
    if (isPaylinePaymentHandledRef.current || !paylineWidgetDataRef.current) {
      return;
    }

    isPaylinePaymentHandledRef.current = true;

    const data = paylineWidgetDataRef.current;
    const token = eventData?.token || data.token;
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    setPaylineToken(null);
    paylineWidgetDataRef.current = null;
    if (removePaylineListenersRef.current) {
      removePaylineListenersRef.current();
    }

    setError(null);
    setWaiting(true);
    setStatus('Vérification du paiement...');

    const processPayment = saveImprint
      ? Payline.api.captureWithToken(apolloClient, {
          token,
          amount: data.amount,
          uuid: data.id,
          reference: data.id,
          customer: {
            firstName: account.firstname,
            lastName: account.lastname,
            email: account.email,
          },
        })
      : // Nouvelle carte (widget hébergé) : on ENREGISTRE le paiement côté serveur, lié à
        // la commande, dès la vérification (recordWidgetPayment) au lieu de seulement le
        // lire (checkPayment). Ainsi, si la réponse se perd, le débit reste récupérable
        // (bookingConfirmIfCaptured) et remboursable (cf. incident LAZZARETTO 19/07).
        Payline.api.recordWidgetPayment(apolloClient, { orderId: data.id, amount: data.amount, token });

    return processPayment
      .then((result) => {
        if (!result) {
          throw new Error('Impossible de vérifier le paiement Payline.');
        }

        if (result.status !== 'success' && result.resultCode !== '00000' && result.resultCode !== '02500') {
          throw new Error(
            `Le paiement a échoué: ${result.resultCode || result.resultMessage || result.shortMessage || result.status || 'Erreur inconnue'}`,
          );
        }

        data.transaction = {
          amount: data.amount,
          provider: 'payline',
          callNo: data.transaction?.callNo || eventData?.authorizationNumber || result.authorizationNumber,
          transactionNo: data.transaction?.transactionNo || eventData?.transactionId || result.transactionId,
          card: result.maskedCardNumber || data.transaction?.card,
          brand: data.transaction?.brand || result.cardBrand,
          country: data.transaction?.country || result.cardCountry,
        };

        if (saveImprint) {
          setStatus('Enregistrement de la carte...');
          // La carte est créée ici, dans la même opération que le paiement. On
          // mémorise son id et on le reporte dans la transaction : sinon
          // `bookingConfirm` écrivait `credit_card_id = NULL`, le lien n'étant posé
          // que pour une carte déjà enregistrée (imprint.id).
          const newCreditCardId = helpers.uuid.createUuid();
          data.transaction.creditCardId = newCreditCardId;
          return CreditCard.api
            .setCreditCard(apolloClient, {
              id: newCreditCardId,
              token: result.token || token,
              number: result.maskedCardNumber,
              name: 'Payline',
              dueDate: result.expiryDate ? result.expiryDate : result.expirationDate ? result.expirationDate : '0000',
              brand: result.cardBrand,
              country: result.cardCountry,
              walletId: result.walletId,
              paymentRecordId: result.transactionId,
            })
            .then(() => {
              sessionStorage.removeItem('payline_saveImprint');
              return CreditCard.api
                .getCreditCards(apolloClient, { accountId: account.id })
                .then(({ creditCards }) => {
                  if (creditCards && creditCards.length > 0) {
                    setImprint(creditCards[0]);
                  }
                })
                .catch(() => {});
            })
            .then(() => {
              setStatus('Nous confirmons votre paiement...');

              return Booking.api.bookingConfirm(apolloClient, {
                id: data.id,
                transaction: data.transaction,
              });
            });
        }

        sessionStorage.removeItem('payline_saveImprint');
        setStatus('Nous confirmons votre paiement...');

        return Booking.api.bookingConfirm(apolloClient, {
          id: data.id,
          transaction: data.transaction,
        });
      })
      .then((booking) => {
        updateBooking({ ...booking, transaction: data.transaction });
        setLockedSlot(null);
        history.replace('/booking/voucher');
      })
      .catch((err) => {
        isPaylinePaymentHandledRef.current = false;
        setWaiting(false);
        setStatus(null);
        setError(err.message);
        setPaylineToken(null);
        paylineWidgetDataRef.current = null;
        setLockedSlot(null);

        // Avant d'annuler : le débit widget a peut-être réussi sans que la réponse nous
        // revienne. On tente de récupérer la réservation (voucher si payé) au lieu de
        // l'annuler d'emblée - l'annulation reste le dernier recours (cf. LAZZARETTO).
        if (data.id && !err.handled) {
          recoverOrCancel(data, err).catch(() => {});
        }
      });
  };

  const handlePaylinePaymentError = (eventData = {}, errorMsg = 'Le paiement a échoué. Veuillez réessayer.') => {
    const widgetData = paylineWidgetDataRef.current;
    const bookingId = widgetData?.id;
    setPaylineToken(null);
    paylineWidgetDataRef.current = null;
    if (removePaylineListenersRef.current) {
      removePaylineListenersRef.current();
    }

    setWaiting(false);
    setStatus(null);
    setError(eventData?.message || errorMsg);
    isPaylinePaymentHandledRef.current = false;
    setLockedSlot(null);

    // Même sur un échec/annulation signalé par le widget, un débit a pu aboutir côté
    // Payline (cas rare mais coûteux) : on vérifie côté serveur avant d'annuler.
    // recoverOrCancel renvoie au voucher si un paiement est retrouvé, sinon annule.
    if (bookingId) {
      recoverOrCancel(widgetData || { id: bookingId }, new Error(eventData?.message || errorMsg)).catch(() => {});
    }
  };

  const handleLockedSlotRelease = () => history.replace('/booking/result');

  if (redirectRef.current) return <Redirect to='/booking' />;

  const playersCount = booking?.players?.length ?? 0;
  const accessoriesCount = booking?.accessories?.length ?? 0;

  return (
    <Screen error={error} loading={loading} title='Paiement par CB'>
      <OverviewHeader
        date={booking.date}
        time={booking.time}
        course={booking.course}
        playersCount={playersCount}
        accessoriesCount={accessoriesCount}
        rightComponent={<LockedSlotReminder onRelease={handleLockedSlotRelease} />}
      />
      {/*
        * Pavé PERSISTANT, et non un simple message. Un golfeur qui revient à cet
        * écran quelques minutes plus tard doit toujours voir que son paiement est
        * passé, sans quoi il le croit perdu et recommence.
        */}
      {paidNotConfirmed && (
        <div
          style={{
            margin: '16px 0',
            padding: 16,
            border: '1px solid #B26A00',
            borderRadius: 8,
            background: '#FFF4E5',
            color: '#7A4A00',
          }}
        >
          <strong>Paiement en cours de vérification</strong>
          <div style={{ marginTop: 6 }}>{PAID_NOT_CONFIRMED_MESSAGE}</div>
        </div>
      )}
      <Payment
        amount={amount}
        golf={golf}
        imprint={imprint}
        currency={currency}
        session3DS={session3DS}
        isPaylineProvider={PaymentProvider.isPayline(golf)}
        payWithNewCard={payWithNewCard}
        payWithNewCardPayline={payWithNewCardPayline}
        payWithCardImprint={payWithCardImprint}
      />
      {paylineToken && (
        <PaylineWidgetFrame
          token={paylineToken}
          options={{ flowType: PAYLINE_FLOW_TYPES.CHECKOUT, amount: amount }}
          title='Payline Widget'
        />
      )}
      <DialogPaymentIP open={waiting} status={status} />
      <DialogWallet3DS
        open={wallet3DS.open}
        threeDSData={wallet3DS.threeDSData}
        onClose={handleWallet3DSClose}
        onComplete={handleWallet3DSComplete}
      />
    </Screen>
  );
};

const mapStateToProps = ({ app, bookingData }) => ({
  golf: app.golf,
  data: bookingData,
  account: app.account,
});

export default connect(mapStateToProps, Booking.actions)(BookingPayment);
